import React, { useEffect, useState } from "react";
import { FaEdit, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import Toast from '../../components/Toast/Toast';
import { getPromoTextAPI, addPromoTextAPI, deletePromoTextAPI } from '../../utils/api';
import Sidebar from "../Sidebar/Sidebar";
import "./PromoText.css";
import AddPromoTextModal from "./components/AddPromoTextModal";

const PromoText = () => {
  const [promoTexts, setPromoTexts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [showModal, setShowModal] = useState(false);
  const [selectedPromoText, setSelectedPromoText] = useState(null);
  const [promoText, setPromoText] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [promoTextToDelete, setPromoTextToDelete] = useState(null);

  const PAGE_LIMIT = 10;

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
  };

  const fetchPromoTexts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getPromoTextAPI({
        page,
        limit: PAGE_LIMIT,
        search: normalizedSearch || "",
      });

      if (response?.status === 200) {
        const list = response.data;
        const totalPagesFromApi = response?.pagination?.total_pages || 1;
        const currentPageFromApi = response?.pagination?.current_page || page;
        setPromoTexts(list);
        setTotalPages(totalPagesFromApi);
        if (currentPageFromApi !== page) setPage(currentPageFromApi);
      } else {
        setError(response?.message || 'Failed to fetch promo texts');
      }
    } catch (err) {
      console.log('Fetch error:', err);
      setError('Failed to fetch promo texts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromoTexts();
  }, [page, searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const normalizedSearch = searchTerm.toLowerCase().trim();
    const totalEntries = promoTexts.length;
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * PAGE_LIMIT;
  const endIdx = startIdx + promoTexts.length;


  const handleAddNew = () => {
    setSelectedPromoText(null);
    setPromoText("");
    setShowModal(true);
    setSaveLoading(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setPromoText("");
    setSelectedPromoText(null);
  };

  const handleDeleteClick = (promoText) => {
    setPromoTextToDelete(promoText);
    setShowDeleteModal(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setPromoTextToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    try {
      if (!promoTextToDelete?._id) {
        showToast("Invalid promo text data", "error");
        return;
      }
      
      const reqData = { promo_text_id: promoTextToDelete._id };
      const response = await deletePromoTextAPI(reqData);
      
      if (response?.status === 200) {
        setShowDeleteModal(false);
        setPromoTextToDelete(null);
        showToast(`Promo text "${promoTextToDelete?.text}" deleted successfully!`, "success");
        await fetchPromoTexts(); // Refresh the list
      } else if (response?.message) {
        showToast(typeof response?.message === 'string'
          ? response?.message
          : 'Failed due to internal error', "error");
      } else {
        showToast('Failed due to internal error', "error");
      }
    } catch (err) {
      showToast('Failed due to internal error', "error");
    }
  };

  const handleSave = async () => {
    if (!promoText.trim()) {
      showToast("Please enter a promo text.", "error");
      return;
    }

    setSaveLoading(true);

    try {
        // Add new promo text
        const reqData = { text: promoText };
        const response = await addPromoTextAPI(reqData);

        if (response?.status === 200) {
          await fetchPromoTexts();
          handleCloseModal();
          showToast("Promo text added successfully!", "success");
        } else if (response?.message) {
          showToast(typeof response?.message === 'string'
            ? response?.message
            : 'Failed due to internal error', "error");
        } else {
          showToast('Failed due to internal error', "error");
        }
    } catch (err) {
      showToast('Failed due to internal error', "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField === field) {
      return sortOrder === "asc" ? <FaSortUp /> : <FaSortDown />;
    }
    return <FaSort />;
  };

  const getSortedData = () => {
    const sorted = [...promoTexts].sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';
      
      // Convert to string for comparison
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
      
      if (sortOrder === "asc") {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });
    return sorted;
  };

  const sortedPromoTexts = getSortedData();

  const getPageItems = () => {
    const items = [];
    const current = Math.max(1, Math.min(page, totalPages));
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) items.push(i);
      return items;
    }

    items.push(1);
    const showLeftDots = current > 4;
    const showRightDots = current < totalPages - 3;

    if (showLeftDots) items.push('dots-left');

    const start = Math.max(2, current - 1);
    const end = Math.min(totalPages - 1, current + 1);
    for (let i = start; i <= end; i++) items.push(i);

    if (showRightDots) items.push('dots-right');

    items.push(totalPages);
    return items;
  };

  return (
    <>
      <Toast toast={toast} />

      <div className="promo-text-layout">
        <Sidebar />
        <div className="promo-text-main-content">
          <div className="promo-text-header-row">
            <input
              type="text"
              className="search-input"
              placeholder="Search by promo text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search promo texts"
            />
            <button
              type="button"
              className="insert-btn"
              onClick={handleAddNew}
              aria-label="Add Promo Text"
            >
              <span>Add Promo Text</span>
            </button>
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading promo texts...</p>
            </div>
          ) : error ? (
            <div className="loading-container">
              <div style={  { color: '#dc2626', fontSize: '1.25rem', marginBottom: '1rem' }}>⚠️</div>
              <p style={{ color: '#dc2626', fontSize: '1.125rem' }}>{error}</p>
            </div>
          ) : totalEntries === 0 ? (
            <div className="empty-state">
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🌍</div>
              <h3>No Promo Texts Found</h3>
              <p>There are no promo texts in the system yet.</p>
              <p>Click "Add Promo Text" to create the first one.</p>
            </div>
          ) : promoTexts.length === 0 ? (
            <div className="empty-state">
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔎</div>
              <h3>No results</h3>
              <p>We couldn't find any promo text matching "{searchTerm}".</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="promo-text-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th 
                        className="sortable-header"
                        onClick={() => handleSort("text")}
                      >
                        <div className="header-content">
                          Promo Text
                          <span className="sort-icon">
                            {getSortIcon("text")}
                          </span>
                        </div>
                      </th>
                      <th className="actions-col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPromoTexts.map((promoText, idx) => (
                      <tr key={promoText._id || `promo-text-${idx}`}>
                        <td>{startIdx + idx + 1}</td>
                        <td>{promoText.text || ''}</td>
                        <td>
                          <div className="promo-text-action-btn-row">
                            <button
                              type="button"
                              className="promo-text-action-btn promo-text-delete-btn"
                              title="Delete Promo Text"
                              onClick={() => handleDeleteClick(promoText)}
                              aria-label="Delete"
                            >
                              <MdDelete />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="table-footer">
                <div className="footer-left">
                  <span>
                    Showing {promoTexts.length > 0 ? startIdx + 1 : 0} to {endIdx} • Page {page} of {totalPages}
                  </span>
                </div>
                <div className="pagination">
                  <button
                    type="button"
                    disabled={page === 1}
                    onClick={() => handlePageChange(page - 1)}
                    aria-label="Previous page"
                  >
                    Previous
                  </button>
                  {getPageItems().map((item, idx) => (
                    typeof item === 'number' ? (
                      <button
                        type="button"
                        key={`p-${item}`}
                        className={page === item ? 'active' : ''}
                        onClick={() => handlePageChange(item)}
                        aria-current={page === item ? 'page' : undefined}
                      >
                        {item}
                      </button>
                    ) : (
                      <span key={`dots-${idx}`} className="ellipsis" aria-hidden="true">…</span>
                    )
                  ))}
                  <button
                    type="button"
                    disabled={page === totalPages}
                    onClick={() => handlePageChange(page + 1)}
                    aria-label="Next page"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Add/Edit Modal */}
            <AddPromoTextModal
            showModal={showModal}
            promoText={promoText}
            setPromoText={setPromoText}
            saveLoading={saveLoading}
            onSave={handleSave}
            onClose={handleCloseModal}
          />

          <ConfirmationModal
            visible={showDeleteModal}
            onConfirm={handleDeleteConfirm}
            onCancel={handleDeleteCancel}
            title="Confirm Deletion"
            message={`Are you sure you want to delete the Promo Text "${promoTextToDelete?.text || ''}"?`}
            confirmText="Delete"
          />
        </div>
      </div>
    </>
  );
};

export default PromoText; 